import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Label } from '../UI/Label';
import { Alert, AlertDescription } from '../UI/Alert';
import {
  ArrowPathIcon as Loader2,
  ShieldCheckIcon as Shield,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

interface TwoFactorVerificationProps {
  tempToken: string;
  onBack?: () => void;
}

const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({ tempToken, onBack }) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const { loginWith2FA } = useAuth();

  const handleVerifyOtp = async () => {
    const expectedLength = useBackupCode ? 8 : 6;
    if (!otp || otp.length !== expectedLength) {
      setError(`Digite um código de ${expectedLength} ${useBackupCode ? 'caracteres' : 'dígitos'}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const success = await loginWith2FA(otp, tempToken);
      if (!success) {
        setError('Código inválido');
      }
      // Se success for true, o AuthContext já vai redirecionar o usuário
    } catch (err: any) {
      setError('Erro na verificação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVerifyOtp();
    }
  };

  return (
    <Card className='w-full max-w-md mx-auto'>
      <CardHeader className='text-center'>
        <div className='mx-auto mb-4 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center'>
          <Shield className='w-6 h-6 text-blue-600' />
        </div>
        <CardTitle>Verificação de Dois Fatores</CardTitle>
        <CardDescription>
          {useBackupCode
            ? 'Digite um dos seus códigos de backup de 8 caracteres'
            : 'Digite o código de 6 dígitos do seu aplicativo autenticador'}
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='otp'>{useBackupCode ? 'Código de Backup' : 'Código de Verificação'}</Label>
          <Input
            id='otp'
            type='text'
            placeholder={useBackupCode ? 'abc12345' : '000000'}
            value={otp}
            onChange={e => {
              if (useBackupCode) {
                setOtp(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8));
              } else {
                setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
              }
            }}
            onKeyPress={handleKeyPress}
            className='text-center text-lg tracking-widest'
            maxLength={useBackupCode ? 8 : 6}
            autoFocus
          />
        </div>

        {error && (
          <Alert variant='destructive'>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className='text-center'>
          <button
            type='button'
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setOtp('');
              setError('');
            }}
            className='text-sm text-blue-600 hover:text-blue-800 underline'
          >
            {useBackupCode ? 'Usar aplicativo autenticador' : 'Usar código de backup'}
          </button>
        </div>

        <div className='flex gap-2'>
          {onBack && (
            <Button variant='outline' onClick={onBack} className='w-full'>
              Voltar
            </Button>
          )}
          <Button
            onClick={handleVerifyOtp}
            disabled={loading || (useBackupCode ? otp.length !== 8 : otp.length !== 6)}
            className='flex-1'
          >
            {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Verificar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TwoFactorVerification;
